import {
  AgentComplianceReviewDocumentQuery,
  GetIngestionDocumentFragment,
  IngestionDocumentValidationErrorReviewFragment,
} from '@client/graphql/__generated__/types';
import { useMemo } from 'react';
import {
  buildComplianceFieldHighlights,
  buildValidationFieldsMap,
  FieldsByContractDocument,
  getValidationItemFieldIds,
  ValidationField,
} from '~/common/pages/Compliance/utils/fieldHighlights.utils';
import {
  getContractDocDisplayName,
  getValidationResultsForDocument,
} from '~/common/pages/Compliance/utils/validation.utils';
import {
  ComplianceSuccessfulCheck,
  ComplianceSuggestion,
} from '../components/types';

export type { ValidationField };

type AgentDocumentDetail = NonNullable<
  AgentComplianceReviewDocumentQuery['agentComplianceReviewDocument']
>;

type ValidationData = Parameters<typeof getValidationResultsForDocument>[1];

export interface AgentDocumentValidation {
  /** Failed checks, in the right-rail "suggestion" framing. */
  suggestions: ComplianceSuggestion[];
  /** Passed checks. */
  successfulChecks: ComplianceSuccessfulCheck[];
  /** Field ids to paint as failed (yellow warnings on the agent page). */
  errorFieldIds: string[];
  /** Field ids to paint as passed (green). */
  successFieldIds: string[];
  /** Per-field message for failed fields. */
  fieldMessages: Record<string, string>;
  /** Page → field boxes for the PDF overlay. */
  validationFieldsMap: Map<number, ValidationField[]>;
  /**
   * How many contract documents the PDF holds — NOT how many produced findings.
   * The lists need the real count to decide whether to show per-document
   * headings: a multi-document PDF where only one document has findings still
   * needs its heading, because that row's page number is relative to that
   * document alone.
   */
  contractDocumentCount: number;
}

const EMPTY: AgentDocumentValidation = {
  suggestions: [],
  successfulChecks: [],
  errorFieldIds: [],
  successFieldIds: [],
  fieldMessages: {},
  validationFieldsMap: new Map(),
  contractDocumentCount: 0,
};

/**
 * Pure derivation of the agent compliance review per-document display —
 * suggestions, successful checks, and PDF field highlights — from the RAW
 * document detail returned by the backend (repositioned `fields`,
 * `contractDocuments`, `contractDocumentPages`, and the raw
 * `validationResults`/`aiValidationResults` arrays).
 *
 * It runs the SAME shared `getValidationResultsForDocument` the compliance
 * drawer + extension use (AI-prioritized split) and the shared
 * `buildComplianceFieldHighlights`/`getValidationItemFieldIds` field-matching,
 * so the agent page never forks that transformation onto the backend.
 *
 * Exported as a plain function (not just the hook) so the checklist-level
 * resubmit can derive each tab's suggestions from imperatively-fetched detail
 * without re-deriving the logic.
 */
export const deriveAgentDocumentValidation = (
  detail: AgentDocumentDetail | null | undefined
): AgentDocumentValidation => {
  if (!detail) {
    return EMPTY;
  }

  const fields = (detail.fields ?? {}) as FieldsByContractDocument;

  // The shared util reads these off a GetIngestionDocumentFragment-shaped
  // document + validation report data; we supply only the fields it touches
  // and cast (the same adapter pattern the extension hook uses).
  const documentLike = {
    documentId: detail.documentId,
    contractDocuments: detail.contractDocuments,
    // `fields` / `contractDocumentPages` are JSON scalars (typed `any` by
    // codegen); narrow to `unknown` before the adapter cast.
    fields: detail.fields as unknown,
    contractDocumentPages: detail.contractDocumentPages as unknown,
  } as unknown as GetIngestionDocumentFragment;

  // Key the synthetic document validation by the real validation uuid so the
  // derived items' `ingestionDocumentValidationId` matches the hidden-review
  // records (whose `ingestionDocumentValidationId` is that same uuid). This
  // lets both the USER-hidden filter (below, via the shared util) and the
  // AGENT-hidden marking key off a single, consistent id.
  const ingestionDocumentValidationId =
    detail.ingestionDocumentValidationId ?? detail.ingestionDocumentId;

  const validationData = {
    documentValidations: [
      {
        id: ingestionDocumentValidationId,
        validationResults: detail.validationResults as unknown,
        aiValidationResults: detail.aiValidationResults as unknown,
      },
    ],
  } as unknown as ValidationData;

  // Hidden reviews come back on the detail (cast — JSON-ish typed by codegen).
  // USER-hidden reviews are filtered OUT of the suggestions (passed to the
  // shared util); AGENT-hidden reviews are KEPT but marked ignored (rendered
  // crossed-out with an Undo affordance).
  const hiddenReviews = detail.hiddenValidationReviews ?? [];
  const userHidden = hiddenReviews.filter((review) => review.source === 'USER');
  const agentHidden = hiddenReviews.filter(
    (review) => review.source === 'AGENT'
  );

  // Adapt USER-hidden reviews to the shape the shared util matches against
  // (an `IngestionDocumentValidationErrorReviewFragment`-like record), keying
  // `ingestionDocumentValidation.id` on the same validation uuid used above.
  const userHiddenReviews = userHidden.map(
    (review) =>
      ({
        ruleUuid: review.ruleUuid ?? undefined,
        contractDocumentUuid: review.contractDocumentUuid ?? undefined,
        ingestionDocumentValidation: {
          id: review.ingestionDocumentValidationId ?? undefined,
        },
        isHidden: true,
      }) as unknown as IngestionDocumentValidationErrorReviewFragment
  );

  const { errors, successes } = getValidationResultsForDocument(
    documentLike,
    validationData,
    undefined,
    userHiddenReviews
  );

  const highlights = buildComplianceFieldHighlights(errors, successes, fields);

  // Contract document uuid → display name, so each suggestion can be labelled
  // with the document its (document-relative) page number belongs to. The agent
  // detail carries form/name/category flat; reshape for the shared namer so the
  // wording matches the compliance drawer.
  //
  // Read defensively even though codegen types `contractDocuments` as non-null:
  // this function also runs over imperatively-fetched detail (the checklist-level
  // resubmit), and every other field it touches is guarded the same way. An
  // absent list just means no group headings.
  const contractDocumentNames = new Map<string, string>(
    (detail.contractDocuments ?? []).map((contractDocument) => [
      contractDocument.id,
      getContractDocDisplayName({
        documentCategory: contractDocument.documentCategory ?? undefined,
        // This page's shape flattens form/name onto the contract document and
        // both are genuinely nullable here, unlike ContractDocumentObject where
        // `documentVersion.document.form` is `String!`. Synthesize the nested
        // shape only when both are present; otherwise omit it so the helper
        // falls back to the category, which is what passing two undefineds
        // achieved before.
        documentVersion:
          contractDocument.form != null && contractDocument.name != null
            ? {
                document: {
                  form: contractDocument.form,
                  name: contractDocument.name,
                },
              }
            : undefined,
      }),
    ])
  );

  const suggestions: ComplianceSuggestion[] = errors.map((error) => {
    // An AGENT-hidden review marks this suggestion as ignored (crossed-out +
    // Undo). Match the same way the USER-hidden filter does: rule + contract
    // document + the validation uuid carried on both sides.
    const agentReview = agentHidden.find(
      (review) =>
        review.ruleUuid === error.ruleUuid &&
        review.contractDocumentUuid === error.contractDocumentUuid &&
        (review.ingestionDocumentValidationId ?? undefined) ===
          (error.ingestionDocumentValidationId ?? undefined)
    );

    return {
      message: error.message,
      // Absolute pages drive the PDF scroll target and the ignore-mutation keys;
      // `documentPageNumbers` is what the row renders (BLO-13411).
      pageNumbers: error.pageNumbers,
      documentPageNumbers: error.documentPageNumbers,
      ruleUuid: error.ruleUuid,
      isAddedByUser: false,
      fieldIds: getValidationItemFieldIds(error, fields),
      // The owning contract document for the per-row "Ignore" hide. Sourced
      // from the raw `ValidationItem.contractDocumentUuid` populated by
      // `getValidationResultsForDocument` (each result's own
      // `contractDocumentUuid`). Left undefined when the error has no single
      // owning contract document — the backend find-or-create tolerates it.
      contractDocumentUuid: error.contractDocumentUuid,
      contractDocumentName: error.contractDocumentUuid
        ? contractDocumentNames.get(error.contractDocumentUuid)
        : undefined,
      isIgnored: agentReview != null,
      reviewUuid: agentReview?.reviewUuid ?? null,
      // The agent's ignore reason, surfaced on hover and in the resubmit note.
      // Left un-defaulted so the three states stay distinct, as the field
      // documents: undefined when the suggestion is open, null when it was
      // ignored without a reason, the text when it has one.
      ignoreReason: agentReview?.message,
    };
  });

  const successfulChecks: ComplianceSuccessfulCheck[] = successes.map(
    (success) => ({
      message: success.message,
      pageNumbers: success.pageNumbers,
      ruleUuid: success.ruleUuid,
      fieldIds: getValidationItemFieldIds(success, fields),
      // Same per-document grouping as the suggestions list, so both cards read
      // the same way and present contract documents in the same PDF order.
      contractDocumentUuid: success.contractDocumentUuid,
      contractDocumentName: success.contractDocumentUuid
        ? contractDocumentNames.get(success.contractDocumentUuid)
        : undefined,
    })
  );

  return {
    suggestions,
    successfulChecks,
    ...highlights,
    validationFieldsMap: buildValidationFieldsMap(fields),
    contractDocumentCount: contractDocumentNames.size,
  };
};

/**
 * Memoized hook wrapper around {@link deriveAgentDocumentValidation} for the
 * selected document's live detail.
 */
export const useAgentDocumentValidation = (
  detail: AgentDocumentDetail | null | undefined
): AgentDocumentValidation =>
  useMemo(() => deriveAgentDocumentValidation(detail), [detail]);
